import * as Sentry from '@sentry/react';
import { supabase } from '@shared/lib/supabase/client'
import type { Database, PortalType } from '@shared/types/database'
import type {
  ZoekJurisprudentieResponse,
  ZoekSuggestiesResponse,
  ZoekStatistiekenResponse,
  JurisprudentieSearchResult,
  PublicLatestCaseResponse,
} from '@shared/types/database-extensions'
import { preprocessSearchQuery } from '@shared/lib/utils/searchUtils'

type Jurisprudentie = Database['public']['Tables']['jurisprudentie']['Row']

// Re-export for backward compatibility
export type { ZoekStatistiekenResponse as ZoekStatistiekenRpcResponse } from '@shared/types/database-extensions'

export interface SearchFilters {
  query?: string
  dateFrom?: Date
  dateTo?: Date
  zaakTypes?: string[]
  instanties?: string[]
  rechtsgebieden?: string[]
  themas?: string[]
  wetsartikelen?: string[]
  sortBy?: 'relevance' | 'date_asc' | 'date_desc' | 'publication_desc'
  dateField?: 'uitspraak_datum' | 'publicatie_datum'
  limit?: number
  offset?: number
  enableHighlighting?: boolean
  portalIds?: PortalType[]  // Multi-portal filtering support (type-safe)
}

export interface SearchResult {
  data: JurisprudentieWithHighlight[]
  count: number
  error?: string
  suggestions?: string[]
}

/**
 * Extended jurisprudentie type with search highlighting and relevance scoring.
 * Uses JurisprudentieSearchResult from database-extensions as the base type
 * to ensure consistency with RPC function return types.
 */
export type JurisprudentieWithHighlight = JurisprudentieSearchResult;

class JurisprudentieService {
  async searchOptimized(filters: SearchFilters): Promise<SearchResult> {
    // Preprocess the search query to remove stopwords
    const originalQuery = filters.query || '';
    const processedQuery = preprocessSearchQuery(originalQuery);

    return await Sentry.startSpan(
      {
        op: 'db.search',
        name: 'Search Jurisprudentie (Optimized)',
        attributes: {
          'search.query': originalQuery,
          'search.query_processed': processedQuery,
          'search.has_filters': !!(filters.instanties?.length || filters.rechtsgebieden?.length || filters.themas?.length),
          'search.sort_by': filters.sortBy || 'relevance',
          'search.limit': filters.limit || 20,
        },
      },
      async () => {
        try {
          // Use the new RPC function for optimized search
          // Note: parameter names must match the SQL function exactly
          const searchResult = await Sentry.startSpan(
            { op: 'db.query', name: 'RPC zoek_jurisprudentie' },
            async () => {
              return await supabase.rpc('zoek_jurisprudentie', {
                zoekterm: processedQuery, // Use preprocessed query without stopwords
                limiet: filters.limit || 20,
                offset_waarde: filters.offset || 0,  // SQL function uses offset_waarde, not offset
                instantie_ids: filters.instanties || null,
                rechtsgebied_ids: filters.rechtsgebieden || null,
                thema_ids: filters.themas || null,
                zaak_type_ids: filters.zaakTypes || null,
                wetsartikel_ids: filters.wetsartikelen || null,
                van_datum: filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : null,
                tot_datum: filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : null,
                sorteer_op: filters.sortBy || 'relevance',  // Pass the sortBy parameter
                p_portal: filters.portalIds?.[0] || null  // Current portal filter (single portal)
              });
            }
          );

          const { data, error } = searchResult;

          if (error) {
            console.warn('RPC zoek_jurisprudentie not available, falling back to standard search:', error)
            throw error
          }

          // The function returns {resultaten: JSON, totaal_aantal: number}
          // Type is now properly inferred from DatabaseWithTypedRpc
          const typedData = data as ZoekJurisprudentieResponse;
          const resultaten = typedData?.resultaten || [];
          const totaalAantal = typedData?.totaal_aantal || 0;

          // Map the RPC response to our interface
          const mappedData = resultaten.map((item: JurisprudentieWithHighlight) => ({
            ...item,
            // Ensure relevance_score is converted to percentage if needed
            relevance_score: item.relevance_score ? item.relevance_score * 100 : undefined
          }))

          // Get search suggestions if query is provided
          let suggestions: string[] = []
          if (filters.query && filters.query.length >= 3) {
            const query = filters.query; // Create a const reference for TypeScript
            const suggestionsResult = await Sentry.startSpan(
              { op: 'db.query', name: 'RPC zoek_suggesties' },
              async () => {
                return await supabase.rpc('zoek_suggesties', {
                  gedeeltelijke_zoekterm: query.slice(0, 50),  // Match SQL parameter name
                  limiet: 10
                });
              }
            );

            if (suggestionsResult.data) {
              // Type is now properly inferred from DatabaseWithTypedRpc
              suggestions = suggestionsResult.data.map((s: ZoekSuggestiesResponse) => s.suggestie);
            }
          }

          // Add span attributes for results
          Sentry.setMeasurement('search.result_count', totaalAantal, 'none');
          Sentry.setMeasurement('search.returned_count', mappedData.length, 'none');

          return {
            data: mappedData as JurisprudentieWithHighlight[],
            count: totaalAantal,  // Use the actual total count from the database
            suggestions
          }
        } catch (error) {
          console.error('Search error:', error)
          Sentry.captureException(error, {
            tags: {
              search_operation: 'optimized',
              has_query: !!filters.query,
            },
          });
          return {
            data: [],
            count: 0,
            error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het zoeken',
          }
        }
      }
    );
  }

  // Keep the original search method for backward compatibility
  async search(filters: SearchFilters): Promise<SearchResult> {
    // Try to use optimized search first if RPC function is available
    // Only attempt if we have a query or specifically want relevance sorting
    if (filters.query || filters.sortBy === 'relevance') {
      try {
        const optimizedResult = await this.searchOptimized(filters)
        if (!optimizedResult.error) {
          return optimizedResult
        }
      } catch (rpcError) {
        // RPC function not available, fall back to standard search
        console.warn('Falling back to standard search due to RPC error:', rpcError)
      }
    }

    try {
      // Build the base query with joins
      let queryString = '*, instanties(*), jurisprudentie_rechtsgebieden!inner(rechtsgebied_id, rechtsgebieden(*)), jurisprudentie_zaak_types!inner(zaak_type_id, zaak_types(*)), jurisprudentie_wetsartikelen(wetsartikelen(*)), jurisprudentie_themas(themas(*))'

      // If we have filters, we need to adjust the query to use inner joins
      const hasRechtsgebiedFilter = filters.rechtsgebieden && filters.rechtsgebieden.length > 0
      const hasZaakTypeFilter = filters.zaakTypes && filters.zaakTypes.length > 0

      if (hasRechtsgebiedFilter && hasZaakTypeFilter) {
        // Both filters: use inner joins for both
        queryString = '*, instanties(*), jurisprudentie_rechtsgebieden!inner(rechtsgebied_id, rechtsgebieden(*)), jurisprudentie_zaak_types!inner(zaak_type_id, zaak_types(*)), jurisprudentie_wetsartikelen(wetsartikelen(*)), jurisprudentie_themas(themas(*))'
      } else if (hasRechtsgebiedFilter) {
        // Only rechtsgebieden filter: use inner join for rechtsgebieden, regular join for zaak_types
        queryString = '*, instanties(*), jurisprudentie_rechtsgebieden!inner(rechtsgebied_id, rechtsgebieden(*)), jurisprudentie_zaak_types(zaak_types(*)), jurisprudentie_wetsartikelen(wetsartikelen(*)), jurisprudentie_themas(themas(*))'
      } else if (hasZaakTypeFilter) {
        // Only zaak type filter: use inner join for zaak_types, regular join for rechtsgebieden
        queryString = '*, instanties(*), jurisprudentie_rechtsgebieden(rechtsgebieden(*)), jurisprudentie_zaak_types!inner(zaak_type_id, zaak_types(*)), jurisprudentie_wetsartikelen(wetsartikelen(*)), jurisprudentie_themas(themas(*))'
      } else {
        // No filters: use regular joins
        queryString = '*, instanties(*), jurisprudentie_rechtsgebieden(rechtsgebieden(*)), jurisprudentie_zaak_types(zaak_types(*)), jurisprudentie_wetsartikelen(wetsartikelen(*)), jurisprudentie_themas(themas(*))'
      }

      let query = supabase
        .from('jurisprudentie')
        .select(queryString, { count: 'exact' })

      // Apply text search if query provided
      if (filters.query && filters.query.trim()) {
        query = query.textSearch('volledige_tekst', filters.query, {
          type: 'websearch',
          config: 'dutch',
        })
      }

      // Date filters - use dateField to determine which date column to filter on
      const dateColumn = filters.dateField || 'uitspraak_datum';

      if (filters.dateFrom) {
        // Ensure we use the date in local timezone, set to start of day
        const fromDate = new Date(filters.dateFrom)
        fromDate.setHours(0, 0, 0, 0)
        const year = fromDate.getFullYear()
        const month = String(fromDate.getMonth() + 1).padStart(2, '0')
        const day = String(fromDate.getDate()).padStart(2, '0')
        query = query.gte(dateColumn, `${year}-${month}-${day}`)
      }
      if (filters.dateTo) {
        // Ensure we use the date in local timezone, set to end of day
        const toDate = new Date(filters.dateTo)
        toDate.setHours(23, 59, 59, 999)
        const year = toDate.getFullYear()
        const month = String(toDate.getMonth() + 1).padStart(2, '0')
        const day = String(toDate.getDate()).padStart(2, '0')
        query = query.lte(dateColumn, `${year}-${month}-${day}`)
      }

      // Portal filter - only show cases from specified portal(s)
      if (filters.portalIds && filters.portalIds.length > 0) {
        query = query.in('portal', filters.portalIds)
      }

      // Instantie filter
      if (filters.instanties && filters.instanties.length > 0) {
        query = query.in('instantie_id', filters.instanties)
      }

      // Rechtsgebieden filter - using the inner join approach
      if (hasRechtsgebiedFilter) {
        query = query.in('jurisprudentie_rechtsgebieden.rechtsgebied_id', filters.rechtsgebieden!)
      }

      // Zaak types filter - using the inner join approach
      if (hasZaakTypeFilter) {
        query = query.in('jurisprudentie_zaak_types.zaak_type_id', filters.zaakTypes!)
      }

      // Sorting
      switch (filters.sortBy) {
        case 'date_asc':
          query = query.order('uitspraak_datum', { ascending: true })
          break
        case 'date_desc':
          query = query.order('uitspraak_datum', { ascending: false })
          break
        case 'publication_desc':
          query = query.order('publicatie_datum', { ascending: false })
          break
        case 'relevance':
          // When searching by relevance, Supabase's text search already orders by relevance
          // But we still add date as secondary sort
          if (filters.query && filters.query.trim()) {
            query = query.order('uitspraak_datum', { ascending: false })
          } else {
            // If no query, fall back to date
            query = query.order('uitspraak_datum', { ascending: false })
          }
          break
        default:
          query = query.order('uitspraak_datum', { ascending: false })
          break
      }

      // Pagination
      const limit = filters.limit || 20
      const offset = filters.offset || 0
      query = query.range(offset, offset + limit - 1)

      const { data, error, count } = await query

      if (error) throw error

      return {
        data: (data || []) as any[],
        count: count || 0,
      }
    } catch (error) {
      console.error('Search error:', error)
      return {
        data: [],
        count: 0,
        error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het zoeken',
      }
    }
  }

  async getById(id: string): Promise<Jurisprudentie | null> {
    // Query jurisprudentie with all related data
    // Include rechtspraak_content for documents using the new content storage pattern
    const { data, error } = await supabase
      .from('jurisprudentie')
      .select(`
        *,
        instanties(*),
        rechtspraak_content(*),
        jurisprudentie_rechtsgebieden(
          rechtsgebieden(*)
        ),
        jurisprudentie_zaak_types(
          zaak_types(*)
        ),
        jurisprudentie_wetsartikelen(
          wetsartikelen(*)
        ),
        jurisprudentie_themas(
          themas(*)
        )
      `)
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching case:', error)
      return null
    }

    // If content is stored in rechtspraak_content, use that instead of inline columns
    if (data && data.rechtspraak_content) {
      const content = data.rechtspraak_content as {
        uitspraak_tekst?: string;
        uitspraak_html?: string;
        inhoudsindicatie?: string;
      };
      return {
        ...data,
        volledige_tekst: content.uitspraak_tekst || data.volledige_tekst,
        volledige_html: content.uitspraak_html || data.volledige_html,
      };
    }

    return data
  }

  async getByEcli(ecli: string): Promise<Jurisprudentie | null> {
    // Query jurisprudentie with all related data
    // Include rechtspraak_content for documents using the new content storage pattern
    const { data, error } = await supabase
      .from('jurisprudentie')
      .select(`
        *,
        instanties(*),
        rechtspraak_content(*),
        jurisprudentie_rechtsgebieden(
          rechtsgebieden(*)
        ),
        jurisprudentie_zaak_types(
          zaak_types(*)
        ),
        jurisprudentie_wetsartikelen(
          wetsartikelen(*)
        ),
        jurisprudentie_themas(
          themas(*)
        )
      `)
      .eq('ecli', ecli)
      .single()

    if (error) {
      console.error('Error fetching case by ECLI:', error)
      return null
    }

    // If content is stored in rechtspraak_content, use that instead of inline columns
    if (data && data.rechtspraak_content) {
      const content = data.rechtspraak_content as {
        uitspraak_tekst?: string;
        uitspraak_html?: string;
        inhoudsindicatie?: string;
      };
      return {
        ...data,
        volledige_tekst: content.uitspraak_tekst || data.volledige_tekst,
        volledige_html: content.uitspraak_html || data.volledige_html,
      };
    }

    return data
  }

  async getRecent(limit: number = 10): Promise<Jurisprudentie[]> {
    const { data, error } = await supabase
      .from('jurisprudentie')
      .select(`
        *,
        instanties(*),
        jurisprudentie_rechtsgebieden(
          rechtsgebieden(*)
        ),
        jurisprudentie_zaak_types(
          zaak_types(*)
        ),
        jurisprudentie_wetsartikelen(
          wetsartikelen(*)
        ),
        jurisprudentie_themas(
          themas(*)
        )
      `)
      .order('publicatie_datum', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Error fetching recent cases:', error)
      return []
    }

    return data || []
  }

  async getRelated(jurisprudentieId: string, limit: number = 5): Promise<Jurisprudentie[]> {
    // First get the rechtsgebieden of the current case
    const { data: currentCase } = await supabase
      .from('jurisprudentie_rechtsgebieden')
      .select('rechtsgebied_id')
      .eq('jurisprudentie_id', jurisprudentieId)

    if (!currentCase || currentCase.length === 0) {
      return []
    }

    const rechtsgebiedIds = currentCase.map((item) => item.rechtsgebied_id)

    // Find other cases with same rechtsgebieden
    const { data, error } = await supabase
      .from('jurisprudentie')
      .select(`
        *,
        instanties(*),
        jurisprudentie_rechtsgebieden!inner(
          rechtsgebied_id
        )
      `)
      .in('jurisprudentie_rechtsgebieden.rechtsgebied_id', rechtsgebiedIds)
      .neq('id', jurisprudentieId)
      .order('uitspraak_datum', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Error fetching related cases:', error)
      return []
    }

    return data || []
  }

  async saveSearchHistory(userId: string, query: string, filters: SearchFilters, resultsCount: number) {
    const { error } = await supabase.from('zoekgeschiedenis').insert({
      gebruiker_id: userId,
      zoekopdracht: query || '',
      filters: filters as any,
      resultaat_aantal: resultsCount,
    })

    if (error) {
      console.error('Error saving search history:', error)
    }
  }

  async getSearchSuggestions(prefix: string): Promise<string[]> {
    if (prefix.length < 2) return []

    try {
      const { data, error } = await supabase.rpc('zoek_suggesties', {
        gedeeltelijke_zoekterm: prefix.slice(0, 50),
        limiet: 10
      })

      if (error) throw error
      // Type is now properly inferred from DatabaseWithTypedRpc
      return data?.map((item) => item.suggestie) || []
    } catch (error) {
      console.error('Error getting search suggestions:', error)
      return []
    }
  }

  async getRelatedTerms(term: string): Promise<string[]> {
    try {
      const { data, error } = await supabase.rpc('gerelateerde_termen', {
        hoofdterm: term,
        limiet: 10
      })

      if (error) throw error
      // Type is now properly inferred from DatabaseWithTypedRpc
      return data?.map((item) => item.gerelateerde_term) || []
    } catch (error) {
      console.error('Error getting related terms:', error)
      return []
    }
  }

  async getSearchStatistics(): Promise<ZoekStatistiekenResponse | null> {
    try {
      const { data, error } = await supabase.rpc('zoek_statistieken')

      if (error) throw error
      // Type is now properly inferred from DatabaseWithTypedRpc
      return data?.[0] || null
    } catch (error) {
      console.error('Error getting search statistics:', error)
      return null
    }
  }

  /**
   * Get the most recent published case for public display (homepage).
   * This function uses a SECURITY DEFINER RPC to bypass RLS,
   * allowing anonymous users to see the latest case.
   */
  async getPublicLatestCase(portalIds: string[] = ['lexlabour']): Promise<PublicLatestCaseResponse | null> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('get_public_latest_case', {
        p_portals: portalIds
      })

      if (error) {
        console.error('Error fetching public latest case:', error)
        return null
      }

      // RPC returns TABLE (array), extract first element
      const result = Array.isArray(data) ? data[0] : data
      return result as PublicLatestCaseResponse | null
    } catch (error) {
      console.error('Error fetching public latest case:', error)
      return null
    }
  }
}

export const jurisprudentieService = new JurisprudentieService()